/**
 * PATH: packages/job-hub/src/jobs/jobHubDataAdapter.tsx
 * FILE_ID: JOBHUB-JOBS-DATA-ADAPTER-001
 * PURPOSE: The single data-source seam for the shared job hub. A host injects
 *          the HTTP client the job-hub read/write hooks call (the Django
 *          platform client from createDjangoJobHubClient) plus the host
 *          capabilities its routes support.
 * WHY:     Every org runs the hub on the Django platform. The old default was
 *          the tenant axios singleton (/sam-api), kept for SAM/Newmark/Steel
 *          shells that no longer exist; any mount that forgot a provider
 *          silently called the wrong backend and fell back to legacy SAM data
 *          (master /schedule workstream chips, change #1052). With no provider
 *          the client now rejects every call loudly, and capabilities are
 *          opt-in.
 * FLOW:
 * ┌───────────────────────────┐  ┌──────────────────────────┐  ┌────────────────┐
 * │ host mount (required       │→ │ JobHubDataAdapterContext  │→ │ useJobHubData / │
 * │ adapter, Django client)    │  │ (default = missing-client)│  │ Mutations call  │
 * └───────────────────────────┘  └──────────────────────────┘  └────────────────┘
 * RELATED:
 * - packages/job-hub/src/jobs/djangoJobHubClient.ts (the client hosts inject)
 * - packages/job-hub/src/jobs/useJobHubData.ts (consumer — read layer)
 * - packages/job-hub/src/jobs/useJobHubMutations.ts (consumer — write layer)
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'

/**
 * The subset of an axios request config the job-hub hooks actually use.
 * Kept intentionally tiny so a non-axios implementation (e.g. a fetch-based
 * Django client) can satisfy it without pulling in axios.
 */
export interface JobHubRequestConfig {
  params?: Record<string, unknown>
  headers?: Record<string, string>
  responseType?: 'blob'
  timeout?: number
}

/** Axios-shaped response; blob paths also consume Content-Disposition. */
export interface JobHubHttpResponse<T = unknown> {
  data: T
  status?: number
  headers?: Record<string, string | undefined>
}

/**
 * The HTTP surface the shared Job Hub hooks depend on. This is the
 * complete set of verbs/signatures used by useJobHubData +
 * useJobHubMutations (get/post/put/patch/delete). Hosts satisfy it with
 * createDjangoJobHubClient.
 *
 * The generic defaults to ``any`` (not ``unknown``) deliberately: the
 * existing hooks read ``r.data`` untyped and feed it straight into
 * ``useQuery<Job>`` / ``new Blob([r.data])`` exactly as axios allowed.
 * Defaulting to ``unknown`` would force a churny re-type of every call
 * site for zero behavioural gain. Call sites that want safety pass an
 * explicit ``<T>``.
 */
export interface JobHubHttpClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any>(url: string, config?: JobHubRequestConfig): Promise<JobHubHttpResponse<T>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any>(url: string, data?: unknown, config?: JobHubRequestConfig): Promise<JobHubHttpResponse<T>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any>(url: string, data?: unknown, config?: JobHubRequestConfig): Promise<JobHubHttpResponse<T>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch<T = any>(url: string, data?: unknown, config?: JobHubRequestConfig): Promise<JobHubHttpResponse<T>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any>(url: string, config?: JobHubRequestConfig): Promise<JobHubHttpResponse<T>>
}

/**
 * Host-app capabilities the shared hub adapts its chrome to. Every flag is
 * opt-in: absent means the surface is off. ``jobWrites`` is the one
 * exception and is read by resolveEffectiveJobCapabilities as a host
 * restriction (absent = writes allowed, subject to job RBAC).
 */
export interface JobHubCapabilities {
  /** Host SPA has a routable /schedule page (full Gantt planner). The
   *  board's "Gantt" deep-link renders only when true. */
  schedulePage?: boolean
  /** Backend can extract invoice candidates from supplier emails. The
   *  InvoiceCandidatesPanel renders only when true; the Django client only
   *  serves a synthetic [] for this surface. */
  invoiceExtraction?: boolean
  /** False makes the host read-only for job writes (no New Job button, no
   *  inline editors, no destructive row actions). Absent = allowed. */
  jobWrites?: boolean
  /** Host SPA has a routable /qdrive filing surface. Gates the right rail's
   *  "Open full filing in QDrive" deep link (S4 rank 13). */
  qdriveFiling?: boolean
  /** Backend can generate DOCX documents for quote sheets. Gates the
   *  per-row "Download DOCX" buttons + context-menu items. */
  documentGeneration?: boolean
  /** Backend serves the Jobs V2 Excel export. */
  excelExport?: boolean
  /** Backend serves the per-job team/access RBAC surface
   *  (/api/jobs/:id/team/ + /access/ — report/views/job_permissions.py). */
  jobTeamAccess?: boolean
  /** Backend hosts the Quotech money-document ledger (claims app:
   *  /api/claims/by-job/ + /api/claims/:id/export_pdf/). Gates the
   *  Invoices-tab "Money documents (ledger)" panel with its PDF download. */
  moneyLedger?: boolean
}

/**
 * The job-hub data adapter. Currently a single ``client`` plus optional
 * host ``capabilities``; modelled as an object (not a bare client) so
 * future, semantic per-domain hooks can be added without changing every
 * call site.
 */
export interface JobHubDataAdapter {
  client: JobHubHttpClient
  capabilities?: JobHubCapabilities
  /**
   * Optional host hook: create a LOGIN for a person just added to the roster
   * (UAT W3, Vibhuti 2026-07-09 — "Add Person creates a person record but not
   * a user account"). Roster Employees deliberately don't imply logins (crew
   * who never open the app); hosts that CAN invite (master/Quotech → the org
   * members endpoint) wire this so the onboard modal offers an explicit
   * "send login invite" step. Absent = the modal states plainly that no
   * login is created and where to grant one.
   */
  inviteMember?: (args: {
    email: string
    firstName: string
    lastName: string
  }) => Promise<{ ok: boolean; message?: string }>
}

/** Raised by every call a job-hub hook makes with no adapter mounted above it. */
export class JobHubAdapterMissingError extends Error {
  readonly method: string
  readonly url: string

  constructor(method: string, url: string) {
    super(
      `Job hub ${method.toUpperCase()} ${url} has no data adapter. ` +
        'Wrap the surface in <JobHubDataAdapterProvider> with createDjangoJobHubClient.',
    )
    this.name = 'JobHubAdapterMissingError'
    this.method = method
    this.url = url
  }
}

function rejectMissing(method: string, url: string): Promise<never> {
  const error = new JobHubAdapterMissingError(method, url)
  console.error('[job-hub] missing data adapter', { method, url })
  return Promise.reject(error)
}

/** The client a hook sees with no provider: every verb rejects loudly. */
export const missingJobHubHttpClient: JobHubHttpClient = {
  get: (url) => rejectMissing('get', url),
  post: (url) => rejectMissing('post', url),
  put: (url) => rejectMissing('put', url),
  patch: (url) => rejectMissing('patch', url),
  delete: (url) => rejectMissing('delete', url),
}

const missingAdapter: JobHubDataAdapter = { client: missingJobHubHttpClient }

const JobHubDataAdapterContext = createContext<JobHubDataAdapter>(missingAdapter)

/**
 * Provider every host wraps a job-hub surface with. When `adapter` is
 * undefined the hooks below see the missing-adapter client, so a host that
 * forgets to inject fails loudly instead of calling a stale backend.
 */
export function JobHubDataAdapterProvider({
  adapter,
  children,
}: {
  adapter?: JobHubDataAdapter
  children: ReactNode
}) {
  // Memoise so a parent re-render doesn't hand the hooks a new context
  // value every time (which would thrash query closures).
  const value = useMemo<JobHubDataAdapter>(() => adapter ?? missingAdapter, [adapter])
  return (
    <JobHubDataAdapterContext.Provider value={value}>
      {children}
    </JobHubDataAdapterContext.Provider>
  )
}

/** Read the active job-hub data adapter (the missing-adapter client if none). */
export function useJobHubDataAdapter(): JobHubDataAdapter {
  return useContext(JobHubDataAdapterContext)
}

/** Read one opt-in capability flag: true only when the host sets it true. */
export function useJobHubCapability(name: keyof JobHubCapabilities): boolean {
  const { capabilities } = useContext(JobHubDataAdapterContext)
  return capabilities?.[name] === true
}
